import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import { mapProductTypeHierarchyToAnalyticsFields } from 'features/cart/utils/cart-analytics-transforms';

import { CartEntryType } from 'generated/graphql-gateway';
import { getStaticPathname } from 'navigation/location/location';
import {
  ClickEventComponentNames,
  CustomEventNames,
  EventTypes,
  logButtonClick,
  logEvent,
  logRBIEvent,
  logRBIProductsDetailsEvent,
} from 'state/amplitude';
import {
  IAmplitudeProduct,
  IAmplitudeProductDetails,
  ICartEntryLoggable,
  ProductItemType,
} from 'state/amplitude/types';
import {
  booleanToString,
  convertToSingleCommerceProduct,
  serializePickupMode,
} from 'state/amplitude/utils';
import { ServiceMode } from 'state/service-mode/types';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';

type CartModalName =
  | 'Minimal cart pricing error'
  | 'Delivery minimum subtotal'
  | 'Cart selection required';

type CartModalActionName = 'Okay' | 'Try Again' | 'Try another store';

type CartUpsellDisplayedEventAttributes = IAmplitudeProductDetails;

type CartUpsellAddedEventAttributes = {
  name: string;
  sanityId: string;
  price?: number;
  upsellItemPosition?: number;
};

type AmplitudeSublevelItem = {
  id: string;
  quantity: number;
};

type CartCheckoutAttributes = {
  'Pickup Mode': string;
  'Cart Data': string;
  'Has Upsell': boolean;
  'Has Donation': boolean;
  'Has Offer': boolean;
  'Has Extra': boolean;
  'Total Amount': number;
  'Product Count': number;
};

type CartAddToCartAttributes = {
  isDonation: boolean;
  isExtra: boolean;
  cartId: string;
  'Is Kiosk': string;
  'Device Time': string;
  'Source Page': string;
  'Is Update': string;
  'Cart Data': string;
  'Picker Aspect Selection': string;
  'Combo Slot Selection': string;
  'Item Modified': string;
  'Total Amount': number;
  'Pickup Mode': string;
};

type CartRemoveFromCartAttributes = CartAddToCartAttributes & {
  itemType: 'product' | 'offer' | 'reward';
};

type CartAddToCartSelectionAttributes = {
  pickerAspectSelection?: boolean;
  comboSlotSelection?: boolean;
  itemModified?: boolean;
};

const buttonClickBaseAttributes = {
  component: ClickEventComponentNames.BUTTON,
};

const toDollars = (priceInCents?: number | null) =>
  typeof priceInCents === 'number' ? priceInCents / 100 : undefined;

const isOfferCartEntry = (cartEntry: ICartEntry) =>
  cartEntry.type === MinimalCartEntryType.discount ||
  cartEntry.type === MinimalCartEntryType.incentive;

const toAmplitudeCartEntryType = (cartEntry: ICartEntry): ICartEntryLoggable['type'] => {
  if (cartEntry.type === MinimalCartEntryType.combo) {
    return CartEntryType.COMBO;
  }

  if (cartEntry.type === MinimalCartEntryType.item) {
    return CartEntryType.ITEM;
  }

  return '' as ICartEntryLoggable['type'];
};

const toLoggableCartEntry = (cartEntry: ICartEntry): ICartEntryLoggable => ({
  _id: cartEntry.id,
  cartId: cartEntry.cartId,
  children: (cartEntry.children ?? []).map(toLoggableCartEntry),
  lineId: cartEntry.cartId ?? cartEntry.id,
  name: cartEntry.name,
  price: toDollars(cartEntry.price?.amount) ?? 0,
  quantity: cartEntry.quantity,
  sanityId: cartEntry.id,
  type: toAmplitudeCartEntryType(cartEntry),
  isExtra: cartEntry.type === MinimalCartEntryType.extra || cartEntry.isExtra,
});

const getUpsellEventAttributes = (
  menuEntry: ILocalizedMenuEntry,
  priceInCents?: number
): CartUpsellDisplayedEventAttributes => {
  const productTypeFields = mapProductTypeHierarchyToAnalyticsFields(menuEntry.productType);

  return {
    name: menuEntry.name?.locale ?? '',
    sanityId: menuEntry.id,
    ...(typeof priceInCents === 'number' ? { price: toDollars(priceInCents) } : {}),
    productType: productTypeFields.productType ?? String(menuEntry.type ?? '').toLowerCase(),
    foodType: productTypeFields.foodType,
    category: productTypeFields.category,
    segment: productTypeFields.segment,
    platform: productTypeFields.platform,
    menuItemName: productTypeFields.menuItemName,
  };
};

const getUpsellAddedEventAttributes = ({
  menuEntry,
  priceInCents,
  upsellItemPosition,
}: {
  menuEntry: ILocalizedMenuEntry;
  priceInCents?: number;
  upsellItemPosition?: number;
}): CartUpsellAddedEventAttributes => ({
  name: menuEntry.name?.locale ?? '',
  sanityId: menuEntry.id,
  ...(typeof priceInCents === 'number' ? { price: toDollars(priceInCents) } : {}),
  ...(typeof upsellItemPosition === 'number' ? { upsellItemPosition } : {}),
});

const logCartButtonClick = (name: string, attributes: Record<string, string> = {}) => {
  logButtonClick({
    attributes: {
      ...buttonClickBaseAttributes,
      name,
      Name: name,
      ...attributes,
    },
  });
};

const getCheckoutProductDescendants = (cartEntry: ICartEntry): ICartEntry[] =>
  (cartEntry.children ?? []).flatMap(child => [child, ...getCheckoutProductDescendants(child)]);

const getCheckoutSublevelItems = (cartEntry: ICartEntry): AmplitudeSublevelItem[] => {
  const subItems = getCheckoutProductDescendants(cartEntry).filter(
    item => item.id !== cartEntry.id && item.type === MinimalCartEntryType.item
  );

  const mergedItemsById = subItems.reduce<Record<string, AmplitudeSublevelItem>>((acc, item) => {
    const currentItem = acc[item.id];

    if (currentItem) {
      currentItem.quantity += item.quantity;
    } else {
      acc[item.id] = { id: item.id, quantity: item.quantity };
    }

    return acc;
  }, {});

  return Object.values(mergedItemsById);
};

const toCheckoutAmplitudeProduct = (cartEntry: ICartEntry): IAmplitudeProduct | null => {
  if (!cartEntry.id) {
    return null;
  }

  const quantity = cartEntry.quantity ?? 1;
  const unitPrice = toDollars(cartEntry.price?.amount) ?? 0;
  const sublevelItems = getCheckoutSublevelItems(cartEntry);
  const itemLevel = sublevelItems.length === 0 ? ProductItemType.Child : ProductItemType.Parent;

  const productAttributes = {
    cartId: cartEntry.cartId || cartEntry.id,
    comboChild: booleanToString(false),
    isDonation: booleanToString(
      cartEntry.type === MinimalCartEntryType.donation || !!cartEntry.isDonation
    ),
    isExtra: booleanToString(cartEntry.type === MinimalCartEntryType.extra || !!cartEntry.isExtra),
    'Item Level': itemLevel,
    sublevelItems: JSON.stringify(sublevelItems),
  };

  return {
    name: cartEntry.name,
    id: cartEntry.id,
    quantity,
    price: unitPrice,
    total_product_amount: unitPrice * quantity,
    custom_attributes: productAttributes,
    ...productAttributes,
  };
};

const getCheckoutProducts = (cartEntries: ICartEntry[]) =>
  cartEntries
    .map(toCheckoutAmplitudeProduct)
    .filter((product): product is IAmplitudeProduct => !!product);

const getCheckoutCartDataItems = (products: IAmplitudeProduct[]) => {
  const cartData = JSON.stringify({
    items: products.map(product => ({
      items: product,
    })),
  });
  const cartDataItemsRegex = /item_\d{3,}/gi;

  return cartData.match(cartDataItemsRegex)?.join() || '';
};

const getCartCheckoutAttributes = ({
  cartEntries,
  products,
  serviceMode,
}: {
  cartEntries: ICartEntry[];
  products: IAmplitudeProduct[];
  serviceMode: ServiceMode;
}): CartCheckoutAttributes => ({
  'Pickup Mode': serializePickupMode(serviceMode),
  'Cart Data': getCheckoutCartDataItems(products),
  'Has Upsell': cartEntries.some(entry => !!entry.isUpsell),
  'Has Donation': cartEntries.some(
    entry => entry.type === MinimalCartEntryType.donation || !!entry.isDonation
  ),
  'Has Offer': cartEntries.some(entry => isOfferCartEntry(entry)),
  'Has Extra': cartEntries.some(
    entry => entry.type === MinimalCartEntryType.extra || !!entry.isExtra
  ),
  'Total Amount': products.reduce((total, product) => total + product.total_product_amount, 0),
  'Product Count': products.length,
});

const getCartAddToCartAttributes = ({
  cartEntry,
  previousCartEntries,
  product,
  serviceMode,
  selectionAttrs,
}: {
  cartEntry: ICartEntry;
  previousCartEntries: ICartEntry[];
  product: IAmplitudeProduct;
  serviceMode: ServiceMode;
  selectionAttrs?: CartAddToCartSelectionAttributes;
}): CartAddToCartAttributes => ({
  isDonation: cartEntry.type === MinimalCartEntryType.donation || !!cartEntry.isDonation,
  isExtra: cartEntry.type === MinimalCartEntryType.extra || !!cartEntry.isExtra,
  cartId: cartEntry.cartId || cartEntry.id,
  'Is Kiosk': booleanToString(false),
  'Device Time': getDeviceTime(),
  'Source Page': getStaticPathname(),
  'Is Update': booleanToString(false),
  'Cart Data': getCheckoutCartDataItems(getCheckoutProducts(previousCartEntries)),
  'Picker Aspect Selection': booleanToString(!!selectionAttrs?.pickerAspectSelection),
  'Combo Slot Selection': booleanToString(!!selectionAttrs?.comboSlotSelection),
  'Item Modified': booleanToString(!!selectionAttrs?.itemModified),
  'Total Amount': product.total_product_amount,
  'Pickup Mode': serializePickupMode(serviceMode),
});

const getCartRemoveFromCartAttributes = ({
  cartEntry,
  previousCartEntries,
  product,
  serviceMode,
  isReward = false,
}: {
  cartEntry: ICartEntry;
  previousCartEntries: ICartEntry[];
  product: IAmplitudeProduct;
  serviceMode: ServiceMode;
  isReward?: boolean;
}): CartRemoveFromCartAttributes => ({
  ...getCartAddToCartAttributes({
    cartEntry,
    previousCartEntries,
    product,
    serviceMode,
  }),
  itemType: isReward ? 'reward' : isOfferCartEntry(cartEntry) ? 'offer' : 'product',
});

export const CartAnalyticsModalNames = {
  PRICING_ERROR: 'Minimal cart pricing error' as CartModalName,
  DELIVERY_MINIMUM_SUBTOTAL: 'Delivery minimum subtotal' as CartModalName,
  SELECTION_REQUIRED: 'Cart selection required' as CartModalName,
};

export const CartAnalyticsModalActionNames = {
  OKAY: 'Okay' as CartModalActionName,
  TRY_AGAIN: 'Try Again' as CartModalActionName,
  TRY_ANOTHER_STORE: 'Try another store' as CartModalActionName,
};

export const CartAnalyticsCommerceEventNames = {
  ADD_TO_CART: CustomEventNames.E_COMMERCE_ADD_TO_CART as const,
  ADD_TO_CART_ITEM: 'eCommerce - AddToCart - Item' as const,
  CHECKOUT: CustomEventNames.E_COMMERCE_CHECKOUT as const,
  CHECKOUT_ITEM: 'eCommerce - Checkout - Item' as const,
  REMOVE_FROM_CART: CustomEventNames.E_COMMERCE_REMOVE_FROM_CART as const,
  REMOVE_FROM_CART_ITEM: 'eCommerce - RemoveFromCart - Item' as const,
};

const getDeviceTime = () => new Date().toTimeString().split(' ')[0];

export const logCartItemDuplicated = () => {
  logCartButtonClick(CustomEventNames.BUTTON_CLICK_DUPLICATE_ITEM);
};

export const logCartItemRemoved = () => {
  logCartButtonClick(CustomEventNames.BUTTON_CLICK_CART_ITEM_REMOVED);
};

export const logCartItemEdited = ({
  cartEntry,
  editUrl,
}: {
  cartEntry: ICartEntry;
  editUrl: string;
}) => {
  logRBIProductsDetailsEvent({
    name: CustomEventNames.EDIT_CART,
    products: [toLoggableCartEntry(cartEntry)],
    attributes: {
      'Product Id': cartEntry.id,
      'Product Name': cartEntry.name,
      'Edit Url': editUrl,
      'Is Reorder': booleanToString(false),
      'Is Offer': booleanToString(isOfferCartEntry(cartEntry)),
    },
  });
};

export const logCartContinuePressed = () => {
  logCartButtonClick(CustomEventNames.BUTTON_CLICK_PLACE_ORDER);
};

export const logCartCheckoutStarted = ({
  cartEntries,
  serviceMode,
}: {
  cartEntries: ICartEntry[];
  serviceMode: ServiceMode;
}) => {
  const products = getCheckoutProducts(cartEntries);

  if (!products.length) {
    return;
  }

  const checkoutAttributes = getCartCheckoutAttributes({
    cartEntries,
    products,
    serviceMode,
  });

  logRBIEvent({
    name: CartAnalyticsCommerceEventNames.CHECKOUT,
    type: EventTypes.Other,
    attributes: {
      ...checkoutAttributes,
      products,
    },
  });

  products.forEach(product => {
    logEvent(CartAnalyticsCommerceEventNames.CHECKOUT_ITEM, EventTypes.Other, {
      ...checkoutAttributes,
      ...convertToSingleCommerceProduct(product),
    });
  });
};

export const logCartAddToCart = ({
  cartEntry,
  previousCartEntries,
  serviceMode,
  selectionAttrs,
}: {
  cartEntry: ICartEntry;
  previousCartEntries: ICartEntry[];
  serviceMode: ServiceMode;
  selectionAttrs?: CartAddToCartSelectionAttributes;
}) => {
  const product = toCheckoutAmplitudeProduct(cartEntry);

  if (!product) {
    return;
  }

  const addToCartAttributes = getCartAddToCartAttributes({
    cartEntry,
    previousCartEntries,
    product,
    serviceMode,
    selectionAttrs,
  });

  logRBIProductsDetailsEvent({
    name: CartAnalyticsCommerceEventNames.ADD_TO_CART,
    products: [toLoggableCartEntry(cartEntry)],
    attributes: {
      wasModified: !!(selectionAttrs?.comboSlotSelection || selectionAttrs?.pickerAspectSelection),
      wasCustomized: !!selectionAttrs?.itemModified,
      ...addToCartAttributes,
      products: [product],
    },
  });

  logRBIEvent({
    name: CartAnalyticsCommerceEventNames.ADD_TO_CART_ITEM,
    type: EventTypes.Other,
    attributes: {
      products: [],
      ...addToCartAttributes,
      ...convertToSingleCommerceProduct(product),
    },
  });
};

export const logCartRemoveFromCart = ({
  cartEntry,
  previousCartEntries,
  serviceMode,
  isReward = false,
}: {
  cartEntry: ICartEntry;
  previousCartEntries: ICartEntry[];
  serviceMode: ServiceMode;
  isReward?: boolean;
}) => {
  const product = toCheckoutAmplitudeProduct(cartEntry);

  if (!product) {
    return;
  }

  const removeFromCartAttributes = getCartRemoveFromCartAttributes({
    cartEntry,
    previousCartEntries,
    product,
    serviceMode,
    isReward,
  });

  logRBIProductsDetailsEvent({
    name: CartAnalyticsCommerceEventNames.REMOVE_FROM_CART,
    products: [toLoggableCartEntry(cartEntry)],
    attributes: {
      ...removeFromCartAttributes,
      products: [product],
    },
  });

  logRBIEvent({
    name: CartAnalyticsCommerceEventNames.REMOVE_FROM_CART_ITEM,
    type: EventTypes.Other,
    attributes: {
      products: [],
      ...removeFromCartAttributes,
      ...convertToSingleCommerceProduct(product),
    },
  });
};

export const logCartDonationViewed = () => {
  logRBIEvent({
    name: CustomEventNames.FOUNDATION_DONATIONS_VIEW,
    type: EventTypes.Other,
    attributes: {},
  });
};

export const logCartUpsellDisplayed = ({
  menuEntry,
  priceInCents,
}: {
  menuEntry: ILocalizedMenuEntry;
  priceInCents?: number;
}) => {
  logRBIEvent({
    name: CustomEventNames.UPSELL_DISPLAYED,
    type: EventTypes.Other,
    attributes: {
      productsDetails: [getUpsellEventAttributes(menuEntry, priceInCents)],
    },
  });
};

export const logCartUpsellAdded = ({
  menuEntry,
  priceInCents,
  upsellItemPosition,
}: {
  menuEntry: ILocalizedMenuEntry;
  priceInCents?: number;
  upsellItemPosition?: number;
}) => {
  logRBIEvent({
    name: CustomEventNames.UPSELL_ADDED,
    type: EventTypes.Other,
    attributes: getUpsellAddedEventAttributes({
      menuEntry,
      priceInCents,
      upsellItemPosition,
    }),
  });
};

export const logCartModalAction = ({
  actionName,
  modalName,
}: {
  actionName: CartModalActionName;
  modalName: CartModalName;
}) => {
  logCartButtonClick(actionName, {
    modalName,
  });
};
